import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Info } from 'lucide-react';

import { useTranslation } from 'react-i18next';

export type TutorialMode = 'full' | 'contract';
type Tab = 'energie' | 'contrat';

interface TutorialStep {
  target: string;
  /** Clé des textes : memberTutorial.steps.<key>.title / .description */
  key: string;
  position: 'left' | 'right' | 'top' | 'bottom' | 'center';
  /** Onglet du dashboard à afficher pour cette étape. */
  tab?: Tab;
}

const BASE_STEPS: TutorialStep[] = [
  { target: '__tutorial_welcome__', key: 'welcome', position: 'center', tab: 'energie' },
  { target: 'tutorial-sidebar-profile', key: 'profile', position: 'right' },
  { target: 'tutorial-sidebar-invoices', key: 'invoices', position: 'right' },
  { target: 'tutorial-sidebar-contact', key: 'contact', position: 'right' },
  { target: 'tutorial-sidebar-settings', key: 'settings', position: 'right' },
  { target: 'tutorial-monthly-chart', key: 'monthly', position: 'top', tab: 'energie' },
  { target: 'tutorial-qh-data', key: 'qh', position: 'top', tab: 'energie' },
  { target: 'tutorial-group-table', key: 'group', position: 'top', tab: 'energie' },
];

/** Démo de l'onglet Contrat : à la fin du guide complet, ou seule (une fois) pour les membres existants. */
const CONTRACT_STEPS: TutorialStep[] = [
  { target: 'tutorial-contract-tab', key: 'contractTab', position: 'bottom', tab: 'contrat' },
  { target: 'tutorial-contract-supplier', key: 'contractSupplier', position: 'right', tab: 'contrat' },
  { target: 'tutorial-contract-siu', key: 'contractSiu', position: 'left', tab: 'contrat' },
  { target: 'tutorial-contract-offer', key: 'contractOffer', position: 'top', tab: 'contrat' },
  { target: 'tutorial-contract-market', key: 'contractMarket', position: 'top', tab: 'contrat' },
];

const CONTRACT_INTRO: TutorialStep = { target: '__tutorial_welcome__', key: 'contractIntro', position: 'center', tab: 'contrat' };

interface MemberTutorialProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: TutorialMode;
  /** Change d'onglet quand une étape se trouve sur un autre onglet. */
  onTabChange?: (tab: Tab) => void;
  /** Étapes absentes pour ce membre (ex. pas d'offre partenaire pour un producteur seul). */
  hiddenTargets?: string[];
}

export function MemberTutorial({ isOpen, onClose, mode = 'full', onTabChange, hiddenTargets = [] }: MemberTutorialProps) {
  const { t } = useTranslation();
  const TUTORIAL_STEPS = (mode === 'contract' ? [CONTRACT_INTRO, ...CONTRACT_STEPS] : [...BASE_STEPS, ...CONTRACT_STEPS])
    .filter(s => !hiddenTargets.includes(s.target));
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const step = TUTORIAL_STEPS[currentStep];

  const updateTargetRect = useCallback((attempt = 0) => {
    if (step.target === '__tutorial_welcome__') {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(`[data-tutorial="${step.target}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);
      }, 350);
    } else if (attempt < 6) {
      // L'onglet vient peut-être de changer : on attend que l'élément soit affiché.
      setTimeout(() => updateTargetRect(attempt + 1), 150);
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (step.tab) onTabChange?.(step.tab);
      updateTargetRect();
    }
  }, [currentStep, isOpen, updateTargetRect]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => updateTargetRect(6);
    const handleScroll = () => updateTargetRect(6);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateTargetRect]);

  if (!isOpen) return null;

  const isLast = currentStep === TUTORIAL_STEPS.length - 1;
  const isFirst = currentStep === 0;
  const isWelcome = step.target === '__tutorial_welcome__';

  const handleNext = () => {
    if (isLast) {
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (isWelcome || !targetRect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const tooltipWidth = 360;
    const tooltipHeight = 220;
    const margin = 16;

    let top: number;
    let left: number;
    let transform = '';

    switch (step.position) {
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.right + margin;
        transform = 'translate(0, 0)';
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - margin;
        transform = 'translate(0, 0)';
        break;
      case 'top':
        top = targetRect.top - tooltipHeight - margin;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        transform = 'translate(0, 0)';
        break;
      case 'bottom':
        top = targetRect.bottom + margin;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        transform = 'translate(0, 0)';
        break;
      default:
        top = targetRect.bottom + margin;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        transform = 'translate(0, 0)';
    }

    // Clamp to viewport
    if (left < margin) left = margin;
    if (left + tooltipWidth > window.innerWidth - margin) {
      left = window.innerWidth - tooltipWidth - margin;
    }
    if (top < margin) top = targetRect.bottom + margin;
    if (top + tooltipHeight > window.innerHeight - margin) {
      top = window.innerHeight - tooltipHeight - margin;
    }

    return { top, left, transform };
  };

  const highlightStyle: React.CSSProperties | null = targetRect
    ? {
        position: 'fixed',
        top: targetRect.top - 4,
        left: targetRect.left - 4,
        width: targetRect.width + 8,
        height: targetRect.height + 8,
        borderRadius: 12,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
        transition: 'all 0.3s ease',
        pointerEvents: 'none',
        zIndex: 60,
      }
    : null;

  return (
    <div ref={overlayRef} className="fixed inset-0 z-50">
      {/* Dark overlay for welcome step */}
      {isWelcome && (
        <div className="absolute inset-0 bg-black/55 transition-opacity" />
      )}

      {/* Highlight box */}
      {highlightStyle && <div style={highlightStyle} />}

      {/* Tooltip */}
      <div
        className="fixed bg-white rounded-2xl shadow-2xl p-6 transition-all duration-300 ease-out flex flex-col"
        style={{
          width: 360,
          ...getTooltipStyle(),
          zIndex: 61,
        }}
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <Info className="w-4 h-4 text-amber-600" />
            </div>
            <span className="text-xs font-medium text-neutral-400">
              {t('memberTutorial.step', { current: currentStep + 1, total: TUTORIAL_STEPS.length })}
            </span>
          </div>
          <button
            onClick={handleSkip}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
            title={t('memberTutorial.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <h3 className="text-lg font-bold text-neutral-900 mb-2">
          {t(`memberTutorial.steps.${step.key}.title`)}
        </h3>
        <p className="text-sm text-neutral-600 leading-relaxed mb-4 flex-1">
          {t(`memberTutorial.steps.${step.key}.description`)}
        </p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-4">
          {TUTORIAL_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === currentStep
                  ? 'w-6 bg-amber-500'
                  : i < currentStep
                  ? 'w-1.5 bg-amber-300'
                  : 'w-1.5 bg-neutral-200'
              }`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={handleSkip}
            className="text-sm text-neutral-500 hover:text-neutral-700 font-medium transition-colors"
          >
            {t('memberTutorial.skip')}
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-neutral-600 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('memberTutorial.previous')}
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
            >
              {isLast ? t('memberTutorial.finish') : t('memberTutorial.next')}
              {isLast ? null : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberTutorial;
